/*
  Bijou Build / Share & Wishlist v1.0
  Standalone theme feature. No dependency on the Buy with Wood option.
  Guest wishlist: localStorage. Shared wishlist: URL query parameter.
  Shopify Ajax product and cart endpoints are locale aware.
*/
(() => {
  'use strict';

  const KEY = 'bb_wishlist_v1';
  const LIMIT = 30;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const productCache = new Map();
  let fallbackItems = [];
  let shareDialog = null;
  let shareContext = null;
  let page = null;
  let sharedItems = null;
  let currentRows = [];
  let currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

  function validItem(item) {
    return item && typeof item.handle === 'string' && /^[a-z0-9-]{1,120}$/i.test(item.handle) &&
      /^[0-9]{1,20}$/.test(String(item.variantId || ''));
  }
  function unique(items) {
    const keys = new Set();
    return (Array.isArray(items) ? items : []).filter(item => {
      if (!validItem(item)) return false;
      const k = item.handle + ':' + item.variantId;
      if (keys.has(k) || keys.size >= LIMIT) return false;
      keys.add(k);
      return true;
    }).map(item => ({handle: item.handle, variantId: String(item.variantId)}));
  }
  function getItems() {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? unique(JSON.parse(raw)) : fallbackItems.slice();
    } catch (_) { return fallbackItems.slice(); }
  }
  function setItems(items) {
    const cleaned = unique(items);
    fallbackItems = cleaned;
    try { localStorage.setItem(KEY, JSON.stringify(cleaned)); } catch (_) { /* Browser storage might be blocked. */ }
    refreshButtons();
    document.dispatchEvent(new CustomEvent('bb:wishlist:change', {detail:{items:cleaned}}));
    if (page && !sharedItems) renderPage();
  }
  function hasItem(item) {
    return getItems().some(saved => saved.handle === item.handle && saved.variantId === item.variantId);
  }
  function pageUrl(handle, variantId) {
    const url = new URL(root + 'products/' + encodeURIComponent(handle), location.origin);
    if (variantId) url.searchParams.set('variant', String(variantId));
    return url.href;
  }
  function wishlistUrl(items) {
    const url = new URL(root + 'pages/wishlist', location.origin);
    url.searchParams.set('wl', unique(items).map(item => item.handle + ':' + item.variantId).join(','));
    return url.href;
  }
  function getVariantFromButton(actions) {
    let id = actions.dataset.bbVariantId || '';
    const formId = actions.dataset.bbFormId;
    let form = formId ? document.getElementById(formId) : null;
    if (!form && actions.classList.contains('bb-sw-actions--pdp')) {
      const scope = actions.closest('section') || document;
      form = scope.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    }
    if (form) {
      const field = form.querySelector('input[name="id"],select[name="id"]');
      if (field && /^[0-9]{1,20}$/.test(String(field.value))) id = field.value;
    }
    return String(id);
  }
  function itemFromActions(actions) {
    return {handle:actions.dataset.bbProductHandle, variantId:getVariantFromButton(actions)};
  }
  function refreshButtons() {
    const items = getItems();
    document.querySelectorAll('[data-bb-wishlist-count]').forEach(el => { el.textContent = String(items.length); });
    document.querySelectorAll('[data-bb-sw-actions]').forEach(actions => {
      const heart = actions.querySelector('[data-bb-wishlist-toggle]');
      if (!heart) return;
      const item = itemFromActions(actions);
      const saved = hasItem(item);
      heart.setAttribute('aria-pressed', saved ? 'true' : 'false');
      const label = heart.querySelector('.bb-sw-btn__label');
      if (label) label.textContent = saved ? 'Saved to Wishlist' : 'Save to Wishlist';
      heart.setAttribute('aria-label', (saved ? 'Remove ' : 'Add ') + (actions.dataset.bbProductTitle || 'product') + (saved ? ' from wishlist' : ' to wishlist'));
    });
  }
  function feedback(text) {
    if (!page) return;
    const el = page.querySelector('[data-bb-page-feedback]');
    if (!el) return;
    el.textContent = text;
    el.hidden = !text;
  }
  async function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const field = document.createElement('textarea');
    field.value = text;
    field.style.cssText = 'position:fixed;left:-10000px;top:0;';
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand('copy');
    field.remove();
    if (!copied) throw Error('Copy failed. Please select the link manually.');
  }
  function ensureDialog() {
    if (shareDialog) return shareDialog;
    const dialog = document.createElement('dialog');
    dialog.className = 'bb-sw-share-dialog';
    dialog.setAttribute('aria-labelledby','bb-sw-share-title');
    dialog.innerHTML = `
      <div class="bb-sw-share-dialog__heading"><h2 id="bb-sw-share-title">Share</h2><button type="button" class="bb-sw-share-dialog__close" data-bb-share-close aria-label="Close sharing options">×</button></div>
      <p class="bb-sw-share-dialog__info" data-bb-share-url></p>
      <div class="bb-sw-share-dialog__options">
        <a data-bb-share-whatsapp target="_blank" rel="noopener noreferrer">WhatsApp</a>
        <a data-bb-share-facebook target="_blank" rel="noopener noreferrer">Facebook</a>
        <a data-bb-share-x target="_blank" rel="noopener noreferrer">X / Twitter</a>
        <button type="button" data-bb-share-native>Messenger / Other Apps</button>
        <button type="button" data-bb-share-copy>Copy Link</button>
      </div>
      <p class="bb-sw-share-dialog__note" data-bb-share-message role="status" aria-live="polite">Messenger is available through the device sharing menu on supported devices.</p>`;
    document.body.appendChild(dialog);
    dialog.querySelector('[data-bb-share-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); });
    dialog.querySelector('[data-bb-share-copy]').addEventListener('click', async () => {
      try {
        await copyText(shareContext.url);
        dialog.querySelector('[data-bb-share-message]').textContent = 'Link copied. You can paste it anywhere.';
      } catch (e) { dialog.querySelector('[data-bb-share-message]').textContent = e.message; }
    });
    dialog.querySelector('[data-bb-share-native]').addEventListener('click', () => {
      const message = dialog.querySelector('[data-bb-share-message]');
      if (navigator.share) {
        navigator.share({title:shareContext.title, text:shareContext.title, url:shareContext.url})
          .catch(e => { if (e.name !== 'AbortError') message.textContent = 'Sharing was unavailable. Try Copy Link instead.'; });
      } else {
        copyText(shareContext.url).then(() => { message.textContent = 'Link copied. Open Messenger and paste the link.'; })
          .catch(() => { message.textContent = 'Use Copy Link to share this in Messenger.'; });
      }
    });
    shareDialog = dialog;
    return dialog;
  }
  function openShare(title, url) {
    const dialog = ensureDialog();
    shareContext = {title,url};
    dialog.querySelector('#bb-sw-share-title').textContent = title;
    dialog.querySelector('[data-bb-share-url]').textContent = url;
    dialog.querySelector('[data-bb-share-whatsapp]').href = 'https://api.whatsapp.com/send?text=' + encodeURIComponent(title + '\n' + url);
    dialog.querySelector('[data-bb-share-facebook]').href = 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(url);
    dialog.querySelector('[data-bb-share-x]').href = 'https://twitter.com/intent/tweet?text=' + encodeURIComponent(title) + '&url=' + encodeURIComponent(url);
    dialog.querySelector('[data-bb-share-message]').textContent = 'Messenger is available through the device sharing menu on supported devices.';
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else { dialog.setAttribute('open',''); dialog.querySelector('[data-bb-share-message]').textContent = 'Select a sharing option below.'; }
  }
  function money(cents) {
    try { return new Intl.NumberFormat(undefined, {style:'currency',currency}).format(Number(cents)/100); }
    catch (_) { return (Number(cents)/100).toFixed(2); }
  }
  function productImage(product, variant) {
    let src = (variant && variant.featured_image && (variant.featured_image.src || variant.featured_image)) || product.featured_image || (product.images && product.images[0]) || '';
    if (typeof src !== 'string' || !src) return '';
    try {
      const url = new URL(src, location.origin);
      return ['https:','http:'].includes(url.protocol) ? url.href : '';
    } catch (_) { return ''; }
  }
  function productFor(handle) {
    if (!productCache.has(handle)) {
      const url = root + 'products/' + encodeURIComponent(handle) + '.js';
      const promise = fetch(url, {credentials:'same-origin'})
        .then(response => { if (!response.ok) throw Error('Product not available'); return response.json(); });
      productCache.set(handle, promise);
    }
    return productCache.get(handle);
  }
  async function getRows(items) {
    return Promise.all(items.map(async item => {
      try {
        const product = await productFor(item.handle);
        const variant = (product.variants || []).find(v => String(v.id) === String(item.variantId));
        return {item,product,variant,available:!!(variant && variant.available),price: variant ? Number(variant.price) : 0};
      } catch (_) { return {item, product:null, variant:null, available:false, price:0}; }
    }));
  }
  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = String(text);
    return node;
  }
  function makeRow(row, isShared) {
    const {item,product,variant,available,price} = row;
    const wrap = el('article','bb-sw-item');
    const href = pageUrl(item.handle, item.variantId);
    const photo = product ? productImage(product, variant) : '';
    const imageLink = el('a'); imageLink.href = href;
    if (photo) {
      const img = el('img','bb-sw-item__image'); img.src = photo; img.alt = product.title;
      img.loading = 'lazy'; imageLink.appendChild(img);
    } else imageLink.appendChild(el('div','bb-sw-item__image bb-sw-item__placeholder','Image unavailable'));
    wrap.appendChild(imageLink);
    const details = el('div','bb-sw-item__details');
    const title = el('a','bb-sw-item__name', product ? product.title : item.handle);
    title.href = href; details.appendChild(title);
    if (variant && variant.title && variant.title !== 'Default Title') details.appendChild(el('div','bb-sw-item__variant',variant.title));
    if (available) details.appendChild(el('div','bb-sw-item__price',money(price)));
    else details.appendChild(el('div','bb-sw-item__status bb-sw-item__status--bad',product ? 'Unavailable or sold out' : 'Product could not be loaded'));
    wrap.appendChild(details);
    const controls = el('div','bb-sw-item__actions');
    const add = el('button',null,'Add to Cart'); add.type='button'; add.disabled=!available;
    add.dataset.bbRowAction = 'add'; add.dataset.bbRowKey = item.handle+':'+item.variantId;
    controls.appendChild(add);
    if (!isShared) {
      const remove = el('button',null,'Remove'); remove.type='button';
      remove.dataset.bbRowAction='remove'; remove.dataset.bbRowKey=item.handle+':'+item.variantId;
      controls.appendChild(remove);
    }
    wrap.appendChild(controls);
    return wrap;
  }
  async function renderPage() {
    if (!page) return;
    const items = sharedItems || getItems();
    const container = page.querySelector('[data-bb-page-items]');
    const footer = page.querySelector('[data-bb-page-footer]');
    const heading = page.querySelector('[data-bb-wishlist-heading]');
    const desc = page.querySelector('[data-bb-wishlist-description]');
    const count = page.querySelector('[data-bb-page-count]');
    const saveShared = page.querySelector('[data-bb-page-save-shared]');
    const shareButton = page.querySelector('[data-bb-page-share]');
    const addAll = page.querySelector('[data-bb-page-add-all]');
    heading.textContent = sharedItems ? 'Shared Wishlist' : 'My Wishlist';
    desc.textContent = sharedItems ? 'A selection of products shared with you.' : 'Products you love, all in one place.';
    saveShared.hidden = !sharedItems;
    count.textContent = items.length + (items.length === 1 ? ' item' : ' items');
    feedback('');
    container.replaceChildren();
    if (!items.length) {
      currentRows=[]; footer.hidden=true;
      const empty = el('div','bb-sw-empty');
      empty.appendChild(el('h2',null,sharedItems ? 'This shared wishlist is empty.' : 'Your wishlist is empty.'));
      empty.appendChild(el('p',null,'Explore products and save your favourites.'));
      const link=el('a',null,'Explore Products'); link.href=root; empty.appendChild(link);
      container.appendChild(empty); return;
    }
    container.appendChild(el('p','bb-sw-loading','Loading current product details...'));
    const rows = await getRows(items);
    /* Ignore stale responses after a quick add/remove action. */
    if (items.map(i=>i.handle+':'+i.variantId).join('|') !== (sharedItems || getItems()).map(i=>i.handle+':'+i.variantId).join('|')) return;
    currentRows = rows;
    container.replaceChildren(...rows.map(row => makeRow(row, !!sharedItems)));
    const total = rows.reduce((sum,row) => sum + (row.available ? row.price : 0), 0);
    page.querySelector('[data-bb-page-total]').textContent = money(total);
    const availableCount = rows.filter(row=>row.available).length;
    addAll.disabled = availableCount === 0;
    shareButton.disabled = rows.length === 0;
    footer.hidden = false;
  }
  async function addToCart(items) {
    if (!items.length) return;
    const response = await fetch(root + 'cart/add.js', {
      method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json','Accept':'application/json'},
      body:JSON.stringify({items:items.map(item=>({id:item.variantId,quantity:1}))})
    });
    const data = await response.json();
    if (!response.ok) throw Error(data.description || data.message || 'Unable to add to cart.');
    /* Optional integration point for custom drawers. If not supplied, cart page is the safe fallback. */
    document.dispatchEvent(new CustomEvent('bb:wishlist:cart-added', {detail:{items,data}}));
    if (typeof window.BBShareWishlist.afterAddToCart === 'function') {
      await window.BBShareWishlist.afterAddToCart({items,data});
    } else {
      window.location.assign(root + 'cart');
    }
  }
  async function handlePageClick(event) {
    const btn = event.target.closest('button');
    if (!btn || !page.contains(btn)) return;
    if (btn.hasAttribute('data-bb-page-share')) {
      const items = sharedItems || getItems();
      if (items.length) openShare('Check out my wishlist',wishlistUrl(items));
      return;
    }
    if (btn.hasAttribute('data-bb-page-save-shared')) {
      const before=getItems();
      setItems([...before,...(sharedItems || [])]);
      feedback('Products saved to your wishlist.');
      return;
    }
    if (btn.hasAttribute('data-bb-page-add-all')) {
      const allowed = currentRows.filter(row=>row.available).map(row=>row.item);
      const skipped = currentRows.length - allowed.length;
      if (!allowed.length) return;
      if (skipped && !window.confirm(skipped+' unavailable item(s) will be skipped. Add the other items to cart?')) return;
      btn.disabled=true; btn.textContent='Adding...';
      try { await addToCart(allowed); }
      catch (e) {feedback(e.message);btn.disabled=false;btn.textContent='Add All Available to Cart';}
      return;
    }
    if (!btn.dataset.bbRowAction) return;
    const row = currentRows.find(row=>row.item.handle+':'+row.item.variantId === btn.dataset.bbRowKey);
    if (!row) return;
    if (btn.dataset.bbRowAction==='remove') {
      setItems(getItems().filter(item=>item.handle+':'+item.variantId !== btn.dataset.bbRowKey));
      return;
    }
    if (btn.dataset.bbRowAction==='add' && row.available) {
      btn.disabled=true;btn.textContent='Adding...';
      try { await addToCart([row.item]); }
      catch (e) {feedback(e.message);btn.disabled=false;btn.textContent='Add to Cart';}
    }
  }
  function initPage() {
    page=document.querySelector('[data-bb-wishlist-page]');
    if (!page) return;
    currency=page.dataset.currency || currency;
    const params=new URL(location.href).searchParams;
    if (params.has('wl')) {
      sharedItems=unique((params.get('wl')||'').split(',').map(entry=>{
        const parts=entry.split(':'); return {handle:parts[0],variantId:parts[1]};
      }));
    }
    page.addEventListener('click',handlePageClick);
    renderPage();
  }
  function init() {
    initPage();
    refreshButtons();
    document.addEventListener('click',event=>{
      const btn=event.target.closest('[data-bb-wishlist-toggle],[data-bb-product-share]');
      if (!btn) return;
      const actions=btn.closest('[data-bb-sw-actions]');
      if (!actions) return;
      event.preventDefault();event.stopPropagation();
      const item=itemFromActions(actions);
      if (!validItem(item)) return;
      if (btn.hasAttribute('data-bb-product-share')) {
        openShare('Check out '+(actions.dataset.bbProductTitle||'this product'),pageUrl(item.handle,item.variantId));
        return;
      }
      const saved=getItems();
      if (hasItem(item)) setItems(saved.filter(x=>x.handle+':'+x.variantId !== item.handle+':'+item.variantId));
      else if (saved.length>=LIMIT) window.alert('You can save up to '+LIMIT+' items in a shareable wishlist.');
      else setItems([...saved,item]);
    });
    document.addEventListener('change',event=>{
      if (event.target.matches('input[name="id"],select[name="id"]')) refreshButtons();
    });
    document.addEventListener('variant:change',refreshButtons);
    document.addEventListener('variant:changed',refreshButtons);
    window.addEventListener('storage',event=>{if (event.key===KEY) { refreshButtons(); if (page && !sharedItems) renderPage(); }});
  }
  window.BBShareWishlist=Object.assign(window.BBShareWishlist||{},{getItems,setItems,openShare});
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',init);
  else init();
})();
